import CryptoJS from 'crypto-js';

/**
 * BTC Blessed Protocol 核心计算逻辑
 */

// 协议参数
export const PROTOCOL_PARAMS = {
  ROUND_INTERVAL: 210,        // 每轮 210 个区块
  BATCH_SIZE: 6,              // 每批 6 个区块
  LUCKY_NUMBERS_PER_ROUND: 7, // 每轮 7 个幸运数字
  ACTIVE_BLOCKS: 42,          // 前 42 个区块生成幸运数字 (7 * 6)
  LOCK_BLOCKS: 6,             // 最后 6 个区块锁定期 (防止回放)
  GENERATING_BLOCKS: 48,      // 前 48 个区块处于生成阶段 (42 + 6)
};

/**
 * 计算轮次编号 (从 1 开始)
 * @param {number} blockHeight - 区块高度
 * @returns {number} 轮次编号
 */
export const calculateRoundNumber = (blockHeight) => {
  return Math.floor(blockHeight / PROTOCOL_PARAMS.ROUND_INTERVAL) + 1;
};

/**
 * 计算轮次开始区块高度
 * @param {number} roundNumber - 轮次编号
 * @returns {number} 轮次开始区块高度
 */
export const getRoundStartHeight = (roundNumber) => {
  return (roundNumber - 1) * PROTOCOL_PARAMS.ROUND_INTERVAL;
};

/**
 * 计算轮次结束区块高度
 * @param {number} roundNumber - 轮次编号
 * @returns {number} 轮次结束区块高度
 */
export const getRoundEndHeight = (roundNumber) => {
  return roundNumber * PROTOCOL_PARAMS.ROUND_INTERVAL - 1;
};

/**
 * 计算当前轮次的进度 (0-209)
 * @param {number} blockHeight - 区块高度
 * @returns {number} 轮次内进度
 */
export const getProgressInRound = (blockHeight) => {
  return blockHeight % PROTOCOL_PARAMS.ROUND_INTERVAL;
};

/**
 * 计算进度百分比
 * @param {number} blockHeight - 区块高度
 * @returns {number} 百分比 (0-100)
 */
export const getProgressPercentage = (blockHeight) => {
  const progress = getProgressInRound(blockHeight);
  return ((progress / PROTOCOL_PARAMS.ROUND_INTERVAL) * 100).toFixed(1);
};

/**
 * 判断区块是否处于生成阶段 (0-47)
 * @param {number} blockHeight - 区块高度
 * @returns {boolean}
 */
export const isInGeneratingPhase = (blockHeight) => {
  const progress = getProgressInRound(blockHeight);
  return progress < PROTOCOL_PARAMS.GENERATING_BLOCKS;
};

/**
 * 判断区块是否处于锁定阶段 (48-209)
 * @param {number} blockHeight - 区块高度
 * @returns {boolean}
 */
export const isInLockedPhase = (blockHeight) => {
  return !isInGeneratingPhase(blockHeight);
};

/**
 * 计算已生成的幸运数字数量
 * @param {number} blockHeight - 区块高度
 * @returns {number} 已生成数量 (0-7)
 */
export const getCompletedLuckyCount = (blockHeight) => {
  const progress = getProgressInRound(blockHeight);
  const completedBatches = Math.floor(progress / PROTOCOL_PARAMS.BATCH_SIZE);
  return Math.min(completedBatches, PROTOCOL_PARAMS.LUCKY_NUMBERS_PER_ROUND);
};

/**
 * 计算指定幸运数字对应的区块范围
 * @param {number} roundNumber - 轮次编号
 * @param {number} luckyIndex - 幸运数字索引 (0-6)
 * @returns {{start: number, end: number}} 区块范围
 */
export const getLuckyNumberBlockRange = (roundNumber, luckyIndex) => {
  const roundStartHeight = getRoundStartHeight(roundNumber);
  const batchStartBlock = roundStartHeight + (luckyIndex * PROTOCOL_PARAMS.BATCH_SIZE);
  return {
    start: batchStartBlock,
    end: batchStartBlock + PROTOCOL_PARAMS.BATCH_SIZE - 1,
  };
};

/**
 * 生成幸运数字
 * @param {Array<string>} merkleRoots - 6 个 Merkle Root 数组
 * @returns {number} 幸运数字 (0-15)
 */
export const generateLuckyNumber = (merkleRoots) => {
  if (!merkleRoots || merkleRoots.length !== PROTOCOL_PARAMS.BATCH_SIZE) {
    throw new Error(`需要 ${PROTOCOL_PARAMS.BATCH_SIZE} 个 Merkle Root`);
  }

  // 拼接 6 个 Merkle Root
  const concatenated = merkleRoots.join('');

  // SHA256 哈希
  const hash = CryptoJS.SHA256(concatenated).toString();

  // 取最后一位十六进制字符
  const lastChar = hash.slice(-1);

  // 转换为数字 (0-15)
  return parseInt(lastChar, 16);
};

/**
 * 获取幸运数字的计算详情
 * @param {Array<Object>} blocks - 6 个区块对象数组
 * @returns {Object} 计算详情
 */
export const getLuckyNumberCalculationDetails = (blocks) => {
  if (!blocks || blocks.length !== PROTOCOL_PARAMS.BATCH_SIZE) {
    throw new Error(`需要 ${PROTOCOL_PARAMS.BATCH_SIZE} 个区块`);
  }

  const merkleRoots = blocks.map(b => b.merkle_root);
  const concatenated = merkleRoots.join('');
  const hash = CryptoJS.SHA256(concatenated).toString();
  const lastChar = hash.slice(-1);
  const luckyNumber = parseInt(lastChar, 16);

  return {
    blocks: blocks.map(b => ({
      height: b.height,
      timestamp: b.timestamp,
      merkle_root: b.merkle_root,
    })),
    concatenated,
    hash,
    lastChar,
    luckyNumber,
  };
};

/**
 * 预估轮次完成时间
 * @param {number} roundNumber - 轮次编号
 * @param {number} currentHeight - 当前区块高度
 * @param {number} avgBlockTime - 平均出块时间(秒) 默认600秒(10分钟)
 * @returns {Date} 预估完成时间
 */
export const estimateRoundCompletionTime = (roundNumber, currentHeight, avgBlockTime = 600) => {
  const roundEndHeight = getRoundEndHeight(roundNumber);
  const remainingBlocks = roundEndHeight - currentHeight;
  const remainingSeconds = remainingBlocks * avgBlockTime;
  return new Date(Date.now() + remainingSeconds * 1000);
};

/**
 * 根据区块高度判断幸运数字状态
 * @param {number} luckyIndex - 幸运数字索引 (0-6)
 * @param {number} currentHeight - 当前区块高度
 * @param {number} roundNumber - 轮次编号
 * @returns {string} 状态: 'completed' | 'generating' | 'pending'
 */
export const getLuckyNumberStatus = (luckyIndex, currentHeight, roundNumber) => {
  const range = getLuckyNumberBlockRange(roundNumber, luckyIndex);
  
  if (currentHeight >= range.end) {
    return 'completed'; // 已完成
  } else if (currentHeight >= range.start) {
    return 'generating'; // 生成中
  } else {
    return 'pending'; // 等待中
  }
};
